import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
    Typography,
    Box,
    Paper,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Chip,
} from '@mui/material';
import {
    Rocket,
    RestartAlt,
    EmojiEvents,
    PlayArrow,
    Pause,
} from '@mui/icons-material';
import ArcadeGameShell from '../ArcadeGameShell';
import { SI_DISPLAY_NAME } from '../siBrand';
import { useArcadeAgentSession } from '../useArcadeAgentSession';
import { useGmSession } from '../useGmSession';
import { GAME_BOILERPLATE } from './AGENT_BOILERPLATE';
import { ARCADE } from '../../../utils/config';
import { useManualRunRecorder } from '../useManualRunRecorder';
import { isGuestUser, useGameKeyGuard, useKeyboardStart } from '../manualPlay';
import ResultView from '../ui/ResultView';
import UiButton from '../ui/Button';
import ManualSaveNotice from '../ManualSaveNotice';
import { useAuth } from '../../../Context/AuthContext';
import { leaderboardEntryScore } from '../arcadeTheme';
import SpaceInvaderScene, {
    SI_PLAYFIELD_SX,
    drawSpaceInvaderScene,
} from '../visuals/SpaceInvaderScene';
import {
    createSpaceInvaderSession,
    deleteSpaceInvaderSession,
    fetchSeedToken,
    moveSpaceInvaderSession,
    INVADER_POINTS,
} from './spaceInvaderGmClient';
import { gmStartErrorMessage } from '../gmLease';
import { COLOR, GAME_ACCENT } from '../ui/tokens';
import SpaceInvaderModePicker from './SpaceInvaderModePicker';
import SiLevelBanner, { SI_MAX_LEVEL, formatSiLevel } from '../visuals/SiLevelBanner';
import SpaceInvaderCoop, { siRoomIdFromLocation } from './SpaceInvaderCoop';

export { INVADER_POINTS };

const GAME_ID = 'space_invader';
const { UPLOAD_URL } = ARCADE.space_invader;
const ACCENT = GAME_ACCENT.space_invader;
const TICK_MS = 50;

const emptyView = () => ({
    player: { x: 480, y: 640, width: 40, height: 40 },
    invaders: [],
    bullets: [],
    score: 0,
    lives: 3,
    level: 1,
    maxLevel: SI_MAX_LEVEL,
    transition: 0,
    won: false,
    isGameOver: false,
    message: '',
});

const SpaceInvaderSolo = ({ headerContent }) => {
    const canvasRef = useRef(null);
    const [playPhase, setPlayPhase] = useState('menu'); // menu | playing | paused | over
    const playPhaseRef = useRef(playPhase);
    const [view, setView] = useState(emptyView);
    const viewRef = useRef(view);
    const actionRef = useRef('stay');
    const fireQueuedRef = useRef(false);
    const inFlightRef = useRef(false);
    const [showGameOverDialog, setShowGameOverDialog] = useState(false);
    // Result of a run ended with End Game (shown instead of the Start button).
    const [manualResult, setManualResult] = useState(null);
    const auth = useAuth();
    useGameKeyGuard(playPhase === 'playing' || playPhase === 'paused');



    const session = useArcadeAgentSession({ gameId: GAME_ID, uploadUrl: UPLOAD_URL });
    const recorder = useManualRunRecorder({ gameId: GAME_ID, session });
    const gm = useGmSession({
        createSession: createSpaceInvaderSession,
        deleteSession: deleteSpaceInvaderSession,
    });

    useEffect(() => { viewRef.current = view; }, [view]);
    useEffect(() => { playPhaseRef.current = playPhase; }, [playPhase]);

    const bestLoggedScore = (() => {
        if (!session.leaderboard?.length) return 0;
        const best = Number(leaderboardEntryScore(session.leaderboard[0]));
        return Number.isFinite(best) ? best : 0;
    })();

    const paint = useCallback((next) => {
        const canvas = canvasRef.current;
        if (!canvas) return;
        const ctx = canvas.getContext('2d');
        if (!ctx) return;
        const bullets = (next.bullets || []).map((b) => ({
            x: b.x,
            y: b.y,
            width: b.width || 4,
            height: b.height || (b.type === 'invader' ? 8 : 10),
            type: b.type || 'player',
            dy: b.dy,
        }));
        drawSpaceInvaderScene(ctx, {
            player: next.player,
            invaders: (next.invaders || []).map((inv) => ({ ...inv, alive: true })),
            bullets,
            bunkers: [],
            level: next.level,
        });
    }, []);

    const applyGm = useCallback((data) => {
        const next = {
            player: data.player || {
                x: data.player_x ?? 480,
                y: data.player_y ?? 640,
                width: 40,
                height: 40,
            },
            invaders: Array.isArray(data.invaders) ? data.invaders : [],
            bullets: Array.isArray(data.bullets) ? data.bullets : [],
            score: typeof data.score === 'number' ? data.score : 0,
            lives: typeof data.lives === 'number' ? data.lives : 3,
            level: typeof data.level === 'number' ? data.level : 1,
            maxLevel: typeof data.max_level === 'number' ? data.max_level : SI_MAX_LEVEL,
            transition: typeof data.transition === 'number' ? data.transition : 0,
            won: Boolean(data.won),
            isGameOver: Boolean(data.is_game_over || data.game_over),
            message: data.message || '',
        };
        viewRef.current = next;
        setView(next);
        paint(next);
        return next;
    }, [paint]);

    const finishOver = useCallback(async (next) => {
        setPlayPhase('over');
        playPhaseRef.current = 'over';
        setShowGameOverDialog(true);
        recorder.finalizeAndUpload({
            score: next.score,
            extra: { reason: next.message || 'game_over', level: next.level, won: next.won },
        });
        await gm.clearSession();
    }, [gm, recorder]);

    const tick = useCallback(async () => {
        if (playPhaseRef.current !== 'playing') return;
        if (viewRef.current.isGameOver) return;
        if (inFlightRef.current) return;
        const sid = gm.sessionIdRef.current;
        if (!sid) return;
        inFlightRef.current = true;
        try {
            let action = actionRef.current || 'stay';
            if (fireQueuedRef.current) {
                action = 'fire';
                fireQueuedRef.current = false;
            }
            const data = await moveSpaceInvaderSession(sid, action);
            const next = applyGm(data);
            recorder.recordTick({
                action,
                player_x: next.player.x,
                player_y: next.player.y,
                invaders: next.invaders,
                bullets: next.bullets,
                score: next.score,
                lives: next.lives,
                level: next.level,
            });
            if (next.isGameOver) {
                await finishOver(next);
            }
        } catch (err) {
            console.warn('Swarm Defense GM tick failed', err);
        } finally {
            inFlightRef.current = false;
        }
    }, [applyGm, finishOver, gm, recorder]);

    const startGame = useCallback(async () => {
        try {
            gm.setBusy(true);
            // Registered players get a server-signed seed so the run can be ranked.
            const signed = isGuestUser(auth) ? null : await fetchSeedToken(auth?.token);
            const data = await gm.createFreshSession(signed ? { seed: signed.seed } : undefined);
            applyGm(data);
            const initState = data.init_state || { player_x: data.player_x, lives: 3, level: 1 };
            recorder.reset({
                init_state: initState,
                ...(signed && initState.seed === signed.seed ? { seed_token: signed.seedToken } : {}),
            });
            setShowGameOverDialog(false);
            setManualResult(null);
            setPlayPhase('playing');
            playPhaseRef.current = 'playing';
            actionRef.current = 'stay';
        } catch (err) {
            console.error(err);
            setView((v) => ({ ...v, message: gmStartErrorMessage(err) }));
        } finally {
            gm.setBusy(false);
        }
    }, [applyGm, auth, gm, recorder]);

    // Enter / Space start from the menu or after End (Space still fires while playing).
    useKeyboardStart({
        active: playPhase === 'menu' || Boolean(manualResult),
        onStart: startGame,
    });

    const endToMenu = useCallback(async () => {
        // Leave 'playing' before clearSession so ticks cannot send another /move
        // after End and re-track the lease (same race as Santa Drop / Pong).
        playPhaseRef.current = 'menu';
        setPlayPhase('menu');
        actionRef.current = 'stay';
        fireQueuedRef.current = false;
        await gm.clearSession();
        const next = emptyView();
        viewRef.current = next;
        setView(next);
        setShowGameOverDialog(false);
        paint(next);
    }, [gm, paint]);

    // End Game: keep the run's result on screen instead of a bare menu.
    const endEarly = useCallback(async () => {
        const wasLive = playPhaseRef.current === 'playing' || playPhaseRef.current === 'paused';
        const last = viewRef.current;
        await endToMenu();
        if (wasLive) setManualResult({ score: last.score, level: last.level });
    }, [endToMenu]);

    const togglePause = useCallback(() => {
        if (playPhaseRef.current === 'playing') {
            setPlayPhase('paused');
            playPhaseRef.current = 'paused';
        } else if (playPhaseRef.current === 'paused') {
            setPlayPhase('playing');
            playPhaseRef.current = 'playing';
        }
    }, []);

    useEffect(() => {
        if (playPhase !== 'playing') return undefined;
        const id = setInterval(() => { tick(); }, TICK_MS);
        return () => clearInterval(id);
    }, [playPhase, tick]);

    useEffect(() => {
        const onKeyDown = (e) => {
            if (e.key === 'Escape') {
                togglePause();
                return;
            }
            if (playPhaseRef.current !== 'playing') return;
            if (['ArrowLeft', 'a', 'A'].includes(e.key)) {
                actionRef.current = 'left';
                e.preventDefault();
            } else if (['ArrowRight', 'd', 'D'].includes(e.key)) {
                actionRef.current = 'right';
                e.preventDefault();
            } else if (e.key === ' ') {
                fireQueuedRef.current = true;
                e.preventDefault();
            }
        };
        const onKeyUp = (e) => {
            if (['ArrowLeft', 'a', 'A', 'ArrowRight', 'd', 'D'].includes(e.key)) {
                if (actionRef.current === 'left' || actionRef.current === 'right') {
                    actionRef.current = 'stay';
                }
            }
        };
        window.addEventListener('keydown', onKeyDown);
        window.addEventListener('keyup', onKeyUp);
        return () => {
            window.removeEventListener('keydown', onKeyDown);
            window.removeEventListener('keyup', onKeyUp);
        };
    }, [togglePause]);

    const { score, lives, level, maxLevel, transition, won } = view;

    return (
        <ArcadeGameShell
            title={SI_DISPLAY_NAME}
            subtitle="Defend the colony from the swarm"
            accentColor={ACCENT}
            headerContent={headerContent}
            boilerplate={GAME_BOILERPLATE.space_invader}
            session={session}
            growPlayfield
        >
            <Box sx={{
                display: 'flex',
                flexDirection: 'column',
                flex: 1,
                minHeight: 0,
                width: '100%',
                gap: 1,
            }}
            >
                <Box sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    gap: 0.75,
                    flexWrap: 'wrap',
                    flexShrink: 0,
                }}
                >
                    <Chip
                        size="small"
                        icon={<EmojiEvents sx={{ fontSize: '0.9rem !important' }} />}
                        label={`Score: ${score}`}
                        sx={{
                            backgroundColor: 'rgba(255,215,0,0.2)',
                            color: '#FFD700',
                            fontSize: '0.7rem',
                            height: 22,
                            border: '1px solid rgba(255,215,0,0.3)',
                            '& .MuiChip-label': { px: 0.75 },
                            '& .MuiChip-icon': { ml: 0.5 },
                        }}
                    />
                    <Chip
                        size="small"
                        icon={<Rocket sx={{ fontSize: '0.9rem !important' }} />}
                        label={`Lives: ${lives}`}
                        sx={{
                            backgroundColor: 'rgba(0,255,0,0.2)',
                            color: '#00ff00',
                            fontSize: '0.7rem',
                            height: 22,
                            border: '1px solid rgba(0,255,0,0.3)',
                            '& .MuiChip-label': { px: 0.75 },
                            '& .MuiChip-icon': { ml: 0.5 },
                        }}
                    />
                    <Chip
                        size="small"
                        data-testid="si-level"
                        label={formatSiLevel(level, maxLevel)}
                        sx={{
                            backgroundColor: 'rgba(255,255,255,0.1)',
                            color: 'white',
                            fontSize: '0.7rem',
                            height: 22,
                            '& .MuiChip-label': { px: 0.75 },
                        }}
                    />
                    <Chip
                        size="small"
                        icon={<EmojiEvents sx={{ fontSize: '0.9rem !important' }} />}
                        label={`Best: ${bestLoggedScore}`}
                        sx={{
                            backgroundColor: 'rgba(255,0,0,0.2)',
                            color: '#ff4444',
                            fontSize: '0.7rem',
                            height: 22,
                            border: '1px solid rgba(255,0,0,0.3)',
                            '& .MuiChip-label': { px: 0.75 },
                            '& .MuiChip-icon': { ml: 0.5 },
                        }}
                    />
                </Box>

                {playPhase === 'menu' && manualResult && (
                    <Box sx={{ display: 'flex', justifyContent: 'center', flexShrink: 0 }}>
                        <ResultView
                            title="Run ended"
                            score={manualResult.score}
                            details={(
                                <Typography variant="body2" sx={{ color: COLOR.textMuted }}>
                                    Level {manualResult.level}
                                </Typography>
                            )}
                            endedManually
                            isGuest={isGuestUser(auth)}
                            accentColor={ACCENT}
                            onPlayAgain={startGame}
                            autoFocus={false}
                        />
                    </Box>
                )}

                {playPhase === 'menu' && !manualResult && (
                    <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1, flexShrink: 0 }}>
                        <UiButton
                            variant="primary"
                            startIcon={<PlayArrow />}
                            onClick={startGame}
                            disabled={gm.busy}
                            sx={{
                                background: 'linear-gradient(135deg, #00ff00, #00aa00)',
                                color: 'black',
                                fontWeight: 'bold',
                                px: 1.75,
                                py: 0.4,
                                minHeight: 28,
                                borderRadius: 1.5,
                                fontSize: '0.8rem',
                                '&:hover': {
                                    background: 'linear-gradient(135deg, #00aa00, #008800)',
                                },
                            }}
                        >
                            Start Game
                        </UiButton>
                    </Box>
                )}

                {playPhase !== 'menu' && (
                    <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1, flexWrap: 'wrap', flexShrink: 0 }}>
                        <UiButton
                            variant="primary"
                            startIcon={playPhase === 'playing' ? <Pause /> : <PlayArrow sx={{ fontSize: '1rem !important' }} />}
                            onClick={togglePause}
                            disabled={playPhase === 'over'}
                            sx={{
                                background: playPhase === 'playing'
                                    ? 'linear-gradient(135deg, #FF6B35, #FF8E35)'
                                    : 'linear-gradient(135deg, #00ff00, #00aa00)',
                                color: 'white',
                                fontWeight: 'bold',
                                px: 1.5,
                                py: 0.35,
                                minHeight: 28,
                                borderRadius: 1.5,
                                fontSize: '0.75rem',
                            }}
                        >
                            {playPhase === 'playing' ? 'Pause' : 'Resume'}
                        </UiButton>
                        <UiButton
                            variant="danger"
                            startIcon={<RestartAlt />}
                            onClick={endEarly}
                            sx={{
                                borderColor: 'rgba(255,255,255,0.3)',
                                color: 'white',
                                fontWeight: 'bold',
                                px: 1.5,
                                py: 0.35,
                                minHeight: 28,
                                borderRadius: 1.5,
                                fontSize: '0.75rem',
                                '&:hover': {
                                    borderColor: 'white',
                                    backgroundColor: 'rgba(255,255,255,0.1)',
                                },
                            }}
                        >
                            End Game
                        </UiButton>
                    </Box>
                )}

                {view.message && playPhase === 'menu' && (
                    <Typography sx={{ color: COLOR.textMuted, textAlign: 'center', fontSize: '0.85rem' }}>
                        {view.message}
                    </Typography>
                )}

                <Box sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    flex: '1 1 auto',
                    minHeight: { xs: 360, lg: 500 },
                    width: '100%',
                }}
                >
                    <Paper sx={{ ...SI_PLAYFIELD_SX, position: 'relative' }}>
                        <SpaceInvaderScene ref={canvasRef} />
                        <SiLevelBanner level={level} maxLevel={maxLevel} transition={transition} won={won && playPhase !== 'menu'} />
                    </Paper>
                </Box>

                <Box sx={{
                    textAlign: 'center',
                    backgroundColor: 'rgba(0,0,0,0.6)',
                    borderRadius: 1.5,
                    px: 1.5,
                    py: 0.75,
                    border: '1px solid rgba(0,255,0,0.3)',
                    flexShrink: 0,
                }}
                >
                    <Typography variant="caption" sx={{ color: '#00ff00', fontWeight: 'bold', display: 'block', mb: 0.25, letterSpacing: 0.4 }}>
                        Controls
                    </Typography>
                    <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, flexWrap: 'wrap' }}>
                        <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.75)' }}>
                            <strong>← →</strong> or <strong>A D</strong> — Move
                        </Typography>
                        <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.75)' }}>
                            <strong>SPACE</strong> — Shoot
                        </Typography>
                    </Box>
                </Box>

                <Dialog
                    open={showGameOverDialog}
                    onClose={() => setShowGameOverDialog(false)}
                    PaperProps={{
                        sx: {
                            backgroundColor: 'rgba(0,0,0,0.9)',
                            backdropFilter: 'blur(10px)',
                            border: '2px solid rgba(255,0,0,0.5)',
                            borderRadius: 3,
                        },
                    }}
                >
                    <DialogTitle sx={{ textAlign: 'center', color: won ? '#00ff88' : '#ff4444' }}>
                        <Typography variant="h4" sx={{ fontWeight: 'bold', mb: 1 }}>
                            {won ? 'YOU WIN' : 'GAME OVER'}
                        </Typography>
                        <Typography variant="h6" sx={{ color: 'white' }}>
                            {won ? `All ${maxLevel} levels cleared!` : 'The swarm broke through!'}
                        </Typography>
                    </DialogTitle>
                    <DialogContent sx={{ textAlign: 'center' }}>
                        <Box sx={{ display: 'flex', justifyContent: 'center', gap: 4, mb: 2 }}>
                            <Box>
                                <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.7)' }}>
                                    Final Score
                                </Typography>
                                <Typography variant="h4" sx={{ color: '#FFD700', fontWeight: 'bold' }}>
                                    {score}
                                </Typography>
                            </Box>
                            <Box>
                                <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.7)' }}>
                                    Level Reached
                                </Typography>
                                <Typography variant="h4" sx={{ color: '#00ff00', fontWeight: 'bold' }}>
                                    {`${level}/${maxLevel}`}
                                </Typography>
                            </Box>
                        </Box>
                        {score >= bestLoggedScore && score > 0 && (
                            <Typography variant="h6" sx={{ color: '#FFD700', fontWeight: 'bold' }}>
                                NEW HIGH SCORE!
                            </Typography>
                        )}
                        <ManualSaveNotice notice={recorder.saveNotice} color={COLOR.text} />
                    </DialogContent>
                    <DialogActions sx={{ justifyContent: 'center', pb: 3 }}>
                        <UiButton
                            onClick={() => {
                                setShowGameOverDialog(false);
                                startGame();
                            }}
                            variant="primary"
                        >
                            Play Again
                        </UiButton>
                        <UiButton
                            onClick={endToMenu}
                            variant="secondary"
                        >
                            Main Menu
                        </UiButton>
                    </DialogActions>
                </Dialog>
            </Box>
        </ArcadeGameShell>
    );
};

/**
 * Space Invader page: Solo (GM session, unchanged) or Online Co-op (gateway
 * room, SpaceInvaderCoop). An invite link (?si_room=<id>) opens Co-op.
 */
const SpaceInvader = () => {
    const [mode, setMode] = useState(() => (siRoomIdFromLocation() ? 'coop' : 'solo'));
    const [coopLive, setCoopLive] = useState(false);
    const picker = (
        <SpaceInvaderModePicker value={mode} onChange={setMode} accentColor={ACCENT} disabled={coopLive} />
    );
    if (mode === 'coop') return <SpaceInvaderCoop headerContent={picker} onLiveChange={setCoopLive} />;
    return <SpaceInvaderSolo headerContent={picker} />;
};

export default SpaceInvader;

export {
    buildSpaceInvaderTick,
    serializeSpaceInvaderBullets,
    collectSpaceInvaderBullets,
} from '../visuals/SpaceInvaderScene';
